import { Result } from "store/reducers/results/reducer";
import { User } from "store/reducers/users/reducer";
import { getDisplayNameOrEmail } from "store/reducers/users/utils";
import { buildInsights } from "store/reducers/results/insights";
import {
  AccuracyChart,
  AsteriskCard,
  Card,
  ConfusionRow,
  CorrectOrderRow,
  DecisivenessCard,
  EasiestCard,
  HalvesCard,
  MoverCard,
  MoverGrid,
  PadTop,
  SectionHeader,
  SummaryCard,
  ToughRow,
} from "./tasting-insights.styles";

export type TastingInsightsProps = {
  results?: Result;
  users?: User[] | null;
};

const TastingInsights = ({ results, users }: TastingInsightsProps) => {
  const insights = buildInsights(results);
  const {
    tastersCount,
    roundAccuracy,
    toughestRounds,
    easiestRound,
    confusion,
    comeback,
    collapse,
    correctOrder,
  } = insights;

  const display = (email?: string | null) => {
    if (!email) return "-";
    const user = users?.find((u) => u.email === email);
    return getDisplayNameOrEmail(user?.displayName, email);
  };

  if (tastersCount === 0 || roundAccuracy.length === 0) {
    return (
      <PadTop>
        <Card style={{ padding: 16 }}>
          <span>Not enough data for insights yet.</span>
        </Card>
      </PadTop>
    );
  }

  return (
    <div>
      <SummaryCard data={insights} />

      <PadTop>
        <SectionHeader label="Accuracy by round" hint={`out of ${tastersCount}`} />
        <AccuracyChart data={insights} />
      </PadTop>

      {toughestRounds.length > 0 && (
        <PadTop>
          <SectionHeader label="Toughest rounds" />
          <Card>
            {toughestRounds.map((r, i) => (
              <ToughRow
                key={r.round}
                round={r.round}
                beer={r.beer}
                of={tastersCount}
                isLast={i === toughestRounds.length - 1}
              />
            ))}
          </Card>
        </PadTop>
      )}

      {easiestRound && (
        <PadTop>
          <SectionHeader label="Easiest round" />
          <EasiestCard
            round={easiestRound.round}
            beer={easiestRound.beer}
            correct={easiestRound.correct}
            of={tastersCount}
          />
        </PadTop>
      )}

      <PadTop>
        <SectionHeader label="First half vs second half" />
        <HalvesCard data={insights} />
      </PadTop>

      <PadTop>
        <SectionHeader label="Decisiveness" hint="avg answer changes per round" />
        <DecisivenessCard data={insights} />
      </PadTop>

      <PadTop>
        <SectionHeader label="Asterisk performance" />
        <AsteriskCard data={insights} />
      </PadTop>

      {confusion.length > 0 && (
        <PadTop>
          <SectionHeader label="Most confused beers" />
          <div>
            {confusion.map((c) => (
              <ConfusionRow
                key={c.round}
                round={c.round}
                actual={c.actual}
                guessed={c.guessed}
                times={c.times}
              />
            ))}
          </div>
        </PadTop>
      )}

      {(comeback || collapse) && (
        <PadTop>
          <SectionHeader label="Movers" />
          <MoverGrid>
            {comeback ? (
              <MoverCard
                tone="pos"
                emoji="🚀"
                label="Biggest comeback"
                name={display(comeback.userEmail)}
                from={comeback.firstHalf}
                to={comeback.secondHalf}
              />
            ) : (
              <span />
            )}
            {collapse ? (
              <MoverCard
                tone="neg"
                emoji="📉"
                label="Biggest collapse"
                name={display(collapse.userEmail)}
                from={collapse.firstHalf}
                to={collapse.secondHalf}
              />
            ) : (
              <span />
            )}
          </MoverGrid>
        </PadTop>
      )}

      {correctOrder.length > 0 && (
        <PadTop>
          <SectionHeader label="The correct order" />
          <Card>
            {correctOrder.map((beer, i) => (
              <CorrectOrderRow
                key={i as any}
                n={i + 1}
                beer={beer}
                isLast={i === correctOrder.length - 1}
              />
            ))}
          </Card>
        </PadTop>
      )}
    </div>
  );
};

export default TastingInsights;
